export const labelsToOptions = (
  labels: Record<string, string>,
  exclude: string[] = [],
) =>
  Object.entries(labels)
    .filter(([key]) => !exclude.includes(key))
    .map(([value, label]) => ({ value, label }));

export const emptySelectOption = {
  value: "",
  label: "-----------",
};
export const DELETION_FAILED_MESSAGE =
  "Unexpected error occurred while deleting.";
// NOTE: If you're using this, it likely means the controller is not configured to correctly return error responses
// Also in constants.js, but added here to address vscode errors
export const RESPONSE_STATUSES = {
  success: "success",
  failure: "failure",
};
export const SPIRE_NOTIFICATION_CENTER_ENABLED = false;

export enum AnnouncementCadence {
  StartOfMonth = "start_of_month",
}

export const ANNOUNCEMENT_CADENCE_LABELS: Record<AnnouncementCadence, string> =
  {
    [AnnouncementCadence.StartOfMonth]: "Start of Every Month",
  };

export enum AnnouncementContextType {
  Email = "email",
  Progress = "progress_email",
  ActionItem = "action_item_email",
  Summary = "summary_email",
}

export const ANNOUNCEMENT_CONTEXT_TYPE_LABELS: Record<
  AnnouncementContextType,
  string
> = {
  [AnnouncementContextType.Email]: "Email",
  [AnnouncementContextType.Progress]: "Progress Email",
  [AnnouncementContextType.ActionItem]: "Action Item Email",
  [AnnouncementContextType.Summary]: "Summary Email",
};
export const ANNOUNCEMENT_CONTEXT_TYPE_BASE_OPTIONS = labelsToOptions(
  ANNOUNCEMENT_CONTEXT_TYPE_LABELS,
  [AnnouncementContextType.Progress],
);
export const SCHEDULED_ANNOUNCEMENT_CONTEXT_TYPE_OPTIONS = labelsToOptions(
  ANNOUNCEMENT_CONTEXT_TYPE_LABELS,
  [AnnouncementContextType.Summary],
);

export enum AnnouncementRecipientGroup {
  ActiveStudents = "active_students",
  AlumniUsers = "alumni_users",
}

export const ANNOUNCEMENT_RECIPIENT_GROUP_LABELS: Record<
  AnnouncementRecipientGroup,
  string
> = {
  [AnnouncementRecipientGroup.ActiveStudents]:
    "Students Who Have Logged In To This Program",
  [AnnouncementRecipientGroup.AlumniUsers]: "Users With The Alumni Role",
};
// Needed due to empty option concat
export const ANNOUNCEMENT_RECIPIENT_GROUP_OPTIONS = labelsToOptions(
  ANNOUNCEMENT_RECIPIENT_GROUP_LABELS,
);

// Shows up in outcome field on level request review
export enum LevelRequestStatus {
  Draft = "draft",
  Pending = "pending",
  InReview = "inReview",
  Approved = "approved",
  ChangesRequested = "changesRequested",
}
// Some labels are for the microprogress, others used on level request index
export const LEVEL_REQUEST_STATUS_LABELS: Record<LevelRequestStatus, string> = {
  [LevelRequestStatus.Draft]: "In progress",
  [LevelRequestStatus.Pending]: "Pending staff review",
  [LevelRequestStatus.InReview]: "In review",
  [LevelRequestStatus.Approved]: "Approved",
  [LevelRequestStatus.ChangesRequested]: "Changes requested",
};

export enum LevelRequestReviewStatus {
  InProgress = "in_progress",
  Ready = "ready_for_release",
  Released = "released",
}
export const LEVEL_REQUEST_REVIEW_STATUS_LABELS = {
  [LevelRequestReviewStatus.InProgress]: "In Progress",
  [LevelRequestReviewStatus.Ready]: "Ready for Release",
  [LevelRequestReviewStatus.Released]: "Released",
};
export const LEVEL_REQUEST_REVIEW_STATUS_BASE_OPTIONS = labelsToOptions(
  LEVEL_REQUEST_REVIEW_STATUS_LABELS,
  [LevelRequestReviewStatus.Released],
);

export enum Engagement {
  None = "none",
  Some = "some",
  Lot = "lot",
}
export const ENGAGEMENT_LABELS: Record<Engagement, string> = {
  [Engagement.None]: "None",
  [Engagement.Some]: "Some",
  [Engagement.Lot]: "A Lot",
};
// Student Opportunity Reflection inputs doesn't use formkit
export const ENGAGEMENT_OPTIONS = labelsToOptions(ENGAGEMENT_LABELS);

export enum ReviewerSignature {
  Program = "use_program_name_and_email",
  Reviewer = "use_reviewer_name_and_email",
}
export const REVIEWER_SIGNATURE_LABELS: Record<ReviewerSignature, string> = {
  [ReviewerSignature.Program]: "Program name and email",
  [ReviewerSignature.Reviewer]: "Reviewer name and email",
};

export enum OpportunityDuration {
  Day = "one_day",
  MultipleDay = "multiple_day",
  Weeks = "weeks",
  Semester = "semester",
  MultipleSemester = "multiple_semester",
}
export const OPPORTUNITY_DURATION_LABELS: Record<OpportunityDuration, string> =
  {
    [OpportunityDuration.Day]: "One Day Event",
    [OpportunityDuration.MultipleDay]: "Multiple Day Event",
    [OpportunityDuration.Weeks]: "Several Weeks",
    [OpportunityDuration.Semester]: "Semester-Long",
    [OpportunityDuration.MultipleSemester]: "Multi-Semester",
  };
// TODO: remove after new student planner removes/updates OpportunityToolbar.vue
export const OPPORTUNITY_DURATION_OPTIONS = labelsToOptions(
  OPPORTUNITY_DURATION_LABELS,
);

export enum OpportunitySource {
  ECoach = "ecoach_sis_api",
  Happenings = "happenings_api",
  Sessions = "sessions_api",
  Participant = "participant",
  Staff = "program_staff",
}
export const OPPORTUNITY_SOURCE_LABELS: Record<OpportunitySource, string> = {
  [OpportunitySource.ECoach]: "ECoach SIS API",
  [OpportunitySource.Happenings]: "Happenings API",
  [OpportunitySource.Sessions]: "Sessions API",
  [OpportunitySource.Participant]: "Participant",
  [OpportunitySource.Staff]: "Staff",
};
export const OPPORTUNITY_SOURCE_OPTIONS = labelsToOptions(
  OPPORTUNITY_SOURCE_LABELS,
);

export enum OpportunityStatus {
  Draft = "draft",
  NeedsReview = "needs_review",
  Published = "published",
  Archived = "archived",
}
export const OPPORTUNITY_STATUS_LABELS: Record<OpportunityStatus, string> = {
  [OpportunityStatus.Draft]: "Draft",
  [OpportunityStatus.NeedsReview]: "Needs Review",
  [OpportunityStatus.Published]: "Published",
  [OpportunityStatus.Archived]: "Archived",
};
export const OPPORTUNITY_STATUS_NEEDS_REVIEW_LABEL_GSI = "Under Review";
// remove if we don't want to alphabetize in opportunityform anymore
export const OPPORTUNITY_STATUS_OPTIONS = labelsToOptions(
  OPPORTUNITY_STATUS_LABELS,
);
export const OPPORTUNITY_STATUS_PARTIAL_OPTIONS = labelsToOptions(
  OPPORTUNITY_STATUS_LABELS,
  [OpportunityStatus.Published, OpportunityStatus.Archived],
);

export enum Audience {
  Participant = "user",
  Reviewer = "reviewer",
}
export const AUDIENCE_LABELS: Record<Audience, string> = {
  [Audience.Participant]: "Participant",
  [Audience.Reviewer]: "Staff Reviewer",
};

export enum PromptContext {
  Level = "level",
  Opportunity = "opportunity",
}
export const PROMPT_CONTEXT_LABELS: Record<PromptContext, string> = {
  [PromptContext.Level]: "Level-Up Request",
  [PromptContext.Opportunity]: "Opportunity Reflection",
};

export const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50];

